import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  Image,
  Form,
  Carousel,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

import { BACKEND_URL } from "constants";
import PhotoThumbnail from "pages/common/photo/PhotoThumbnail";

import { loadPhotos, photoDeleted } from "../../features/photo/photoSlice";
import { getUserState, getPhotoState } from "../../app/selector";

import { ReactComponent as Next } from "../../resources/caret-right-fill.svg";
import { ReactComponent as Prev } from "../../resources/caret-left-fill.svg";
import { ReactComponent as Delete } from "../../resources/trash.svg";

const profilePicturePlaceholder = require("../../resources/ProfilePicturePlaceholder.jpg");

const defaultVisibility = {
  single_rating_enabled: false,
  gallery_enabled: false,
  is_main: false,
};

// ==========================================
// HELPER COMPONENTS
// ==========================================

const TooltipLabel = React.forwardRef(({ id, children, ...props }, ref) => (
  <Tooltip ref={ref} id={id} {...props}>
    <strong>{children}</strong>
  </Tooltip>
));

const PhotoActions = ({
  visibility,
  onToggleSingleRating,
  onToggleGallery,
  onSetMain,
  onDelete,
}) => (
  <div className="profile-picture-actions">
    <OverlayTrigger
      placement="top"
      trigger={["hover", "focus"]} // <-- Add "focus" here
      overlay={<TooltipLabel id="single-rating">Visible in Single Rating</TooltipLabel>}
    >
      <Button
        type="button"
        variant={visibility.single_rating_enabled ? "primary" : "outline-primary"}
        disabled={visibility.is_main}
        className="round-button delete-button"
        onClick={onToggleSingleRating}
      >
        S
      </Button>
    </OverlayTrigger>

    <OverlayTrigger
      placement="top"
      trigger={["hover", "focus"]} // <-- Add "focus" here
      overlay={<TooltipLabel id="gallery">Visible in Gallery</TooltipLabel>}
    >
      <Button
        type="button"
        variant={visibility.gallery_enabled ? "success" : "outline-success"}
        disabled={visibility.is_main}
        className="round-button delete-button"
        onClick={onToggleGallery}
      >
        G
      </Button>
    </OverlayTrigger>

    <OverlayTrigger
      placement="top"
      trigger={["hover", "focus"]} // <-- Add "focus" here
      overlay={<TooltipLabel id="main-photo">Set as Main Photo</TooltipLabel>}
    >
      <Button
        type="button"
        variant={visibility.is_main ? "warning" : "outline-warning"}
        disabled={visibility.is_main}
        className="round-button delete-button"
        onClick={onSetMain}
      >
        M
      </Button>
    </OverlayTrigger>

    <OverlayTrigger
      placement="top"
      trigger={["hover", "focus"]} // <-- Add "focus" here
      overlay={<TooltipLabel id="delete">Delete</TooltipLabel>}
    >
      <Button
        type="button"
        variant="outline-danger"
        className="round-button delete-button"
        onClick={onDelete}
      >
        <Delete className="delete-icon" />
      </Button>
    </OverlayTrigger>
  </div>
);

const PhotoCarousel = ({
  photos,
  index,
  setIndex,
  showActions = false,
  visibility = defaultVisibility,
  onToggleSingleRating,
  onToggleGallery,
  onSetMain,
  onDelete,
}) => {
  const numOfPhotos = photos.length;
  const currentPhoto = photos[index];

  return (
    <div className="profile-picture bg-light rounded">
      {showActions && numOfPhotos > 0 && (
        <PhotoActions
          visibility={visibility}
          onToggleSingleRating={onToggleSingleRating}
          onToggleGallery={onToggleGallery}
          onSetMain={onSetMain}
          onDelete={onDelete}
        />
      )}

      <div className="profile-picture-carousel">
        <Carousel
          className="photo-slider"
          activeIndex={index}
          onSelect={setIndex}
          interval={null}
          nextIcon={<Next width="64" height="64" className="drop-shadow" />}
          prevIcon={<Prev width="64" height="64" className="drop-shadow" />}
          controls={numOfPhotos > 1}
          indicators={numOfPhotos > 1}
        >
          {numOfPhotos > 0 ? (
            photos.map((photo) => (
              <Carousel.Item key={photo.photo_id}>
                <div className="profile-photo-square">
                  <PhotoThumbnail
                    className="single-rating"
                    thumbnailClass="profile-photo-image"
                    src={`data:image/jpeg;base64,${photo.photo}`}
                    photoId={photo.photo_id}
                  />
                </div>
              </Carousel.Item>
            ))
          ) : (
            <Carousel.Item key="placeholder">
              <div className="profile-photo-square">
                <Image
                  className="profile-photo-image"
                  src={profilePicturePlaceholder}
                  alt="ProfilePicture"
                  fluid
                  rounded
                />
              </div>
            </Carousel.Item>
          )}
        </Carousel>
      </div>

      {numOfPhotos > 0 && (
        <Form.Label className="indicator">
          {index + 1} / {numOfPhotos}
        </Form.Label>
      )}

      {currentPhoto && (
        <div className="statistic">
          <Form.Label className="do-the-same">
            {currentPhoto.num_do} Users do the same
          </Form.Label>
          <Form.Label className="would-do-the-same">
            {currentPhoto.num_would} Users would like to do the same
          </Form.Label>
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

const ProfilePicture = () => {
  const dispatch = useDispatch();
  const { view_user_id, view_photo_id } = useParams();

  const isViewMode = Boolean(view_user_id);
  const { user } = useSelector(getUserState);
  const { photoLoaded, photoById } = useSelector(getPhotoState);

  const [viewPhotoById, setViewPhotoById] = useState({});
  const [index, setIndex] = useState(0);

  const [optimisticUpdate, setOptimisticUpdate] = useState(null);

  const userId = isViewMode ? view_user_id : user?.user_id;

  const photos = useMemo(() => {
    return Object.values(isViewMode ? viewPhotoById : photoById);
  }, [isViewMode, viewPhotoById, photoById]);

  const currentPhoto = photos[index];

  const visibility = useMemo(() => {
    if (!currentPhoto) return defaultVisibility;

    if (optimisticUpdate && optimisticUpdate.photo_id === currentPhoto.photo_id) {
      return optimisticUpdate.visibility;
    }

    return {
      single_rating_enabled: Boolean(currentPhoto.single_rating_enabled),
      gallery_enabled: Boolean(currentPhoto.gallery_enabled),
      is_main: Boolean(currentPhoto.is_main),
    };
  }, [currentPhoto, optimisticUpdate]);

  const reloadPhotos = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await axios.post(`${BACKEND_URL}/photos/by_user_id`, {
        user_id: userId,
      });

      if (isViewMode) {
        const photosById = response.data.reduce((acc, photo) => {
          acc[photo.photo_id] = photo;
          return acc;
        }, {});

        setViewPhotoById(photosById);

        if (view_photo_id) {
          const selectedIndex = response.data.findIndex(
            (photo) => String(photo.photo_id) === String(view_photo_id)
          );
          if (selectedIndex !== -1) setIndex(selectedIndex);
        }
      } else {
        dispatch(loadPhotos({ photos: response.data }));
      }

      setOptimisticUpdate(null);
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, isViewMode, userId, view_photo_id]);

  useEffect(() => {
    if (isViewMode || !photoLoaded) reloadPhotos();
  }, [isViewMode, photoLoaded, reloadPhotos]);

  useEffect(() => {
    if (photos.length === 0) {
      setIndex(0);
      return;
    }
    if (index >= photos.length) setIndex(photos.length - 1);
  }, [photos.length, index]);

  const applyUpdate = async (visibilityPatch) => {
    if (!currentPhoto) return;
    try {
      await axios.post(`${BACKEND_URL}/update-photo-visibility`, {
        photo_id: currentPhoto.photo_id,
        ...visibilityPatch,
      });
      await reloadPhotos();
    } catch (error) {
      console.error(error);
      setOptimisticUpdate(null);
    }
  };

  const handleToggle = (event, visibilityPatch) => {
    // 1. Instantly set the optimistic UI
    const nextVisibility = { ...visibility, ...visibilityPatch };
    setOptimisticUpdate({
      photo_id: currentPhoto.photo_id,
      visibility: nextVisibility
    });

    // 2. Fire the backend update
    applyUpdate(visibilityPatch);

    // 3. Android Chrome Fix: Delay the blur slightly so Android 
    // doesn't re-focus the button immediately after the click event resolves.
    const target = event ? event.currentTarget : null;
    setTimeout(() => {
      if (target && typeof target.blur === 'function') {
        target.blur();
      }
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 50);
  };

  const handleToggleSingleRating = (e) =>
    handleToggle(e, { single_rating_enabled: !visibility.single_rating_enabled });

  const handleToggleGallery = (e) =>
    handleToggle(e, { gallery_enabled: !visibility.gallery_enabled });

  const handleSetMain = (e) =>
    handleToggle(e, { single_rating_enabled: true, gallery_enabled: true, is_main: true });

  const handleDelete = async (event) => {
    event.preventDefault();
    if (!currentPhoto) return;

    try {
      await axios.post(`${BACKEND_URL}/delete-photo`, {
        photo_id: currentPhoto.photo_id,
      });
      dispatch(photoDeleted({ photo: currentPhoto }));
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <PhotoCarousel
      photos={photos}
      index={index}
      setIndex={setIndex}
      showActions={!isViewMode}
      visibility={visibility}
      onToggleSingleRating={handleToggleSingleRating}
      onToggleGallery={handleToggleGallery}
      onSetMain={handleSetMain}
      onDelete={handleDelete}
    />
  );
};

export default ProfilePicture;